import { useEffect, useMemo, useState } from "react";
import { useNotification } from "@/shared/hooks/useNotification";
import { getErrorMessage, getFieldErrors } from "@/shared/utils/errorHandler";
import { useConfirm } from "@/shared/contexts/confirm-context";
import { customersApi } from "@/features/customers/services/customersApi";
import { useCounties, usePaymentTerms } from "@/shared/hooks/useMetaData";
import { useDebounce } from "@/shared/hooks/useDebounce";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, FieldLabel } from "@/components/ui/field";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Plus, Users } from "lucide-react";
import {
  useCreateCustomerGroupFromCriteria,
  useAssignCustomersByCriteria,
} from "../hooks/useCustomerGroups";

/**
 * Call-center "Create customer group by…" flow: pick a matrix of criteria
 * (region, town, customer type), preview how many customers match, then create
 * the group and bulk-assign the matching customers to it. Only active
 * customers are ever picked (fixed isActive:true filter) and membership is a
 * snapshot — later edits stay manual; the same dialog doubles as "add matching
 * customers to this group" for existing groups.
 */
export const CustomerGroupCriteriaDialog = ({
  /** Present = assign mode (top up an existing group); absent = create mode. */
  groupId,
  groupName,
  triggerLabel,
  triggerIcon,
}: {
  groupId?: string;
  groupName?: string;
  triggerLabel?: string;
  triggerIcon?: boolean;
}) => {
  const isAssignMode = Boolean(groupId);

  const [open, setOpen] = useState(false);
  const [criteria, setCriteria] = useState<{
    county: string;
    town: string;
    customerType: string;
  }>({ county: "", town: "", customerType: "" });
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [defaultPaymentTerms, setDefaultPaymentTerms] = useState("COD");
  const [defaultCreditLimit, setDefaultCreditLimit] = useState(0);
  const [defaultDiscountPercentage, setDefaultDiscountPercentage] = useState(0);
  const [isDefault, setDefault] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const notify = useNotification();
  const confirm = useConfirm();
  const countiesQuery = useCounties();
  const paymentTermsQuery = usePaymentTerms();

  const hasAnyCriterion =
    criteria.county !== "" ||
    criteria.town.trim() !== "" ||
    criteria.customerType !== "";

  // Preview count — server-side DB count over the same criteria matrix the
  // create/assign will use, so the number shown is exactly what will happen.
  // Active customers only: group building never targets inactive records.
  const debouncedCriteria = useDebounce(criteria, 400);
  const previewCriteria = useMemo(
    () => ({
      county: debouncedCriteria.county || undefined,
      town: debouncedCriteria.town.trim() || undefined,
      customerType: debouncedCriteria.customerType || undefined,
      isActive: true as const,
    }),
    [debouncedCriteria]
  );
  const hasPreviewCriteria =
    previewCriteria.county !== undefined ||
    previewCriteria.town !== undefined ||
    previewCriteria.customerType !== undefined;

  const [matchCount, setMatchCount] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState(false);

  useEffect(() => {
    if (!open || !hasPreviewCriteria || !isPreviewCriteriaValid(previewCriteria)) {
      setMatchCount(null);
      return;
    }
    let cancelled = false;
    setCountLoading(true);
    customersApi
      .getCustomersCount(previewCriteria)
      .then((count) => {
        if (!cancelled) setMatchCount(count);
      })
      .catch(() => {
        if (!cancelled) setMatchCount(null);
      })
      .finally(() => {
        if (!cancelled) setCountLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, previewCriteria]);

  const setCriterion = (field: keyof typeof criteria, value: string) => {
    setCriteria((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.criteria;
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasAnyCriterion) {
      setFieldErrors({
        criteria: ["Pick at least one filter — e.g. a region — before creating the group"],
      });
      return;
    }

    const criteriaPayload = {
      county: criteria.county || undefined,
      town: criteria.town.trim() || undefined,
      customerType: criteria.customerType || undefined,
      isActive: true,
    };

    const descriptionPayload =
      description.trim() ||
      (criteria.county
        ? criteria.town.trim()
          ? `Customers in ${criteria.county} (${criteria.town.trim()})`
          : `Customers in ${criteria.county}`
        : "Built from matching customers");

    if (isAssignMode) {
      const verb = matchCount === 1 ? "customer matches" : "customers match";
      confirm({
        title: "Add customers to group?",
        description:
          matchCount === null
            ? `Matching customers will be added to “${groupName}”.`
            : `${matchCount} ${verb} your filters and will be added to “${groupName}”. Customers already in the group are skipped.`,
        confirmLabel: "Add customers",
        onConfirm: () =>
          assignMutation
            .mutateAsync({ groupId: groupId!, criteria: criteriaPayload })
            .then((result) => {
              notify.success(
                result.assignedCount === 0
                  ? "No new customers matched — group unchanged"
                  : `${result.assignedCount} customer${result.assignedCount === 1 ? "" : "s"} added to “${groupName}”`
              );
              handleClose();
            })
            .catch((error: unknown) => {
              setFieldErrors(getFieldErrors(error) ?? {});
              notify.error(getErrorMessage(error));
              throw error;
            }),
      });
      return;
    }

    if (!name.trim()) {
      setFieldErrors({ name: ["Group name is required"] });
      return;
    }

    confirm({
      title: "Create customer group?",
      description:
        matchCount === null
          ? `“${name.trim()}” will be created and matching customers assigned to it.`
          : `“${name.trim()}” will be created and ${matchCount} matching customer${matchCount === 1 ? "" : "s"} assigned to it.`,
      confirmLabel: "Create group",
      onConfirm: () =>
        createMutation
          .mutateAsync({
            name: name.trim(),
            description: descriptionPayload,
            defaultPaymentTerms,
            defaultCreditLimit,
            defaultDiscountPercentage,
            isDefault,
            criteria: criteriaPayload,
          })
          .then((result) => {
            notify.success(
              `Customer group “${result.group.name}” created with ${result.assignedCount} customer${result.assignedCount === 1 ? "" : "s"}`
            );
            handleClose();
          })
          .catch((error: unknown) => {
            const fe = getFieldErrors(error);
            if (fe) setFieldErrors(fe);
            notify.error(getErrorMessage(error));
            throw error;
          }),
    });
  };

  const handleClose = () => {
    setOpen(false);
    setCriteria({ county: "", town: "", customerType: "" });
    setName("");
    setDescription("");
    setDefaultPaymentTerms("COD");
    setDefaultCreditLimit(0);
    setDefaultDiscountPercentage(0);
    setDefault(false);
    setFieldErrors({});
    setMatchCount(null);
  };

  const criteriaSection = (
    <div>
      <h3 className="text-lg font-semibold mb-4">Pick who to group by</h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
        <Field>
          <FieldLabel htmlFor="criteria-county">Region (County)</FieldLabel>
          {countiesQuery.isLoading ? (
            <div className="h-10 bg-gray-200 rounded animate-pulse" />
          ) : (
            <Select
              value={criteria.county || "none"}
              onValueChange={(value) => setCriterion("county", value === "none" ? "" : value)}
            >
              <SelectTrigger id="criteria-county" className="w-full">
                <SelectValue placeholder="Any region" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Any region</SelectItem>
                {countiesQuery.data?.map((county) => (
                  <SelectItem key={county.code} value={county.name}>
                    {county.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>

        <Field>
          <FieldLabel htmlFor="criteria-town">Town</FieldLabel>
          <Input
            id="criteria-town"
            value={criteria.town}
            onChange={(e) => setCriterion("town", e.target.value)}
            placeholder="e.g. Westlands (optional)"
          />
        </Field>

        <Field>
          <FieldLabel htmlFor="criteria-type">Customer Type</FieldLabel>
          <Select
            value={criteria.customerType || "any"}
            onValueChange={(value) => setCriterion("customerType", value === "any" ? "" : value)}
          >
            <SelectTrigger id="criteria-type" className="w-full">
              <SelectValue placeholder="Any type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any type</SelectItem>
              <SelectItem value="Individual">Individual</SelectItem>
              <SelectItem value="Organization">Organization</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>

      <p className="text-xs text-muted-foreground mt-2">
        Only active customers are included.
      </p>

      <div className="mt-3 rounded-md border bg-muted/40 px-3 py-2 text-sm">
        {!hasAnyCriterion ? (
          <span className="text-muted-foreground">
            Choose at least one filter to see a preview.
          </span>
        ) : countLoading ? (
          <span className="text-muted-foreground">Checking matching customers…</span>
        ) : matchCount === null ? (
          <span className="text-muted-foreground">
            Couldn’t load a preview count — you can still continue.
          </span>
        ) : matchCount === 0 ? (
          <span className="text-muted-foreground">No active customers match these filters.</span>
        ) : (
          <span>
            <strong>{matchCount.toLocaleString()}</strong>{" "}
            active customer{matchCount === 1 ? "" : "s"} match
            {matchCount === 1 ? "es" : ""} your filters
          </span>
        )}
      </div>
      {fieldErrors.criteria && (
        <div className="text-red-500 text-xs mt-1">{fieldErrors.criteria.join(", ")}</div>
      )}
    </div>
  );

  const groupSettingsSection = !isAssignMode && (
    <div>
      <h3 className="text-lg font-semibold mb-4">Group details & defaults</h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
        <Field>
          <FieldLabel htmlFor="criteria-group-name">
            Group Name <span className="text-red-500">*</span>
          </FieldLabel>
          <Input
            id="criteria-group-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Nakuru Stockists"
          />
          {fieldErrors.name && (
            <div className="text-red-500 text-xs mt-1">{fieldErrors.name.join(", ")}</div>
          )}
        </Field>

        <Field>
          <FieldLabel htmlFor="criteria-group-default">Default Group</FieldLabel>
          <div className="flex items-center space-x-2 mt-2">
            <Checkbox id="criteria-group-default" checked={isDefault} onCheckedChange={(checked) => setDefault(Boolean(checked))} />
            <Label htmlFor="criteria-group-default" className="cursor-pointer">
              Set as default for new customers
            </Label>
          </div>
        </Field>

        <Field>
          <FieldLabel htmlFor="criteria-payment-terms">
            Payment Terms <span className="text-red-500">*</span>
          </FieldLabel>
          {paymentTermsQuery.isLoading ? (
            <div className="h-10 bg-gray-200 rounded animate-pulse" />
          ) : (
            <Select value={defaultPaymentTerms} onValueChange={setDefaultPaymentTerms}>
              <SelectTrigger id="criteria-payment-terms" className="w-full">
                <SelectValue placeholder="Select Payment Terms" />
              </SelectTrigger>
              <SelectContent>
                {paymentTermsQuery.data?.map((term) => (
                  <SelectItem key={term.value} value={term.value}>
                    {term.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>

        <Field>
          <FieldLabel htmlFor="criteria-credit-limit">
            Credit Limit <span className="text-red-500">*</span>
          </FieldLabel>
          <Input
            id="criteria-credit-limit"
            type="number"
            value={defaultCreditLimit}
            onChange={(e) => setDefaultCreditLimit(parseFloat(e.target.value) || 0)}
          />
        </Field>

        <Field className="col-span-2">
          <FieldLabel htmlFor="criteria-discount">
            Default Discount Percentage <span className="text-red-500">*</span>
          </FieldLabel>
          <div className="flex items-center gap-2">
            <Input
              id="criteria-discount"
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={defaultDiscountPercentage}
              onChange={(e) => setDefaultDiscountPercentage(parseFloat(e.target.value) || 0)}
            />
            <span className="text-sm text-gray-500">%</span>
          </div>
        </Field>
      </div>
    </div>
  );

  const createMutation = useCreateCustomerGroupFromCriteria();
  const assignMutation = useAssignCustomersByCriteria();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={isAssignMode ? "ghost" : "default"} className={isAssignMode ? "w-full justify-start" : "gap-2"}>
          {isAssignMode ? (
            <>
              {triggerIcon && <Users className="w-4 h-4 mr-2" />}
              {triggerLabel ?? "Add customers by criteria"}
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" />
              Create Group By…
            </>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isAssignMode ? `Add customers to “${groupName}”` : "Create Customer Group By…"}
          </DialogTitle>
          <DialogDescription>
            {isAssignMode
              ? "Pick filters; every matching active customer will be bulk-assigned to this group."
              : "Pick filters (region, town, type); matching active customers become the group’s members in one step."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {!isAssignMode && (
            <Field>
              <FieldLabel htmlFor="criteria-description">Description</FieldLabel>
              <Input
                id="criteria-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe this customer group (auto-filled from picks)"
              />
            </Field>
          )}

          {criteriaSection}
          {groupSettingsSection}

          <Button
            type="submit"
            className="w-full"
            disabled={createMutation.isPending || assignMutation.isPending}
          >
            {isAssignMode
              ? assignMutation.isPending
                ? "Adding…"
                : `Add ${matchCount != null ? `${matchCount} ` : ""}matching customers`
              : createMutation.isPending
                ? "Creating…"
                : `Create group${matchCount != null ? ` and assign ${matchCount}` : ""}`}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

function isPreviewCriteriaValid(criteria: {
  county?: string;
  town?: string;
  customerType?: string;
}): boolean {
  return Boolean(criteria.county || criteria.town || criteria.customerType);
}
